import { gql } from '@apollo/client';

export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats {
    getDashboardStats {
      totalImages
      activeImages
      failedBuilds
      scheduledBlueprints
      criticalVulnerabilities
      buildSuccessRate
      deprecatedCount
      recentBuilds {
        name
        phase
        startedAt
        finishedAt
      }
      recentImages {
        id
        name
        blueprintName
        osType
        architecture
        status
        createdAt
      }
      upcomingSchedules {
        name
        schedule
        enabled
        lastRun
        blueprintName
      }
    }
  }
`;

export const GET_IMAGES = gql`
  query GetImages($limit: Int, $nextToken: String, $filter: ListImagesFilter) {
    listImages(limit: $limit, nextToken: $nextToken, filter: $filter) {
      items {
        id
        amiId
        name
        blueprintName
        blueprintVersion
        imageType
        osType
        architecture
        status
        createdAt
        thumbnailUrl
      }
      nextToken
    }
  }
`;

export const GET_IMAGE = gql`
  query GetImage($id: ID!) {
    getImage(id: $id) {
      id
      amiId
      name
      description
      blueprintName
      blueprintVersion
      imageType
      osType
      osVersion
      architecture
      status
      createdAt
      workflowName
      platforms {
        id
        platformName
        region
        imageId
        organization
        cloudProvider
        status
        criticalCount
        highCount
        mediumCount
        lowCount
        compliant
        createdAt
      }
    }
  }
`;

export const GET_IMAGE_PLATFORM = gql`
  query GetImagePlatform($id: ID!) {
    getImagePlatform(id: $id) {
      id
      platformName
      region
      imageId
      organization
      cloudProvider
      criticalCount
      highCount
      mediumCount
      lowCount
      compliant
      cves {
        id
        cveId
        title
        description
        severity
        score
        sourceUrl
        fixAvailable
        packageName
        packageVersion
        fixedVersion
      }
    }
  }
`;

export const GET_IMAGE_CVES = gql`
  query GetImageCves($imagePlatformId: ID!, $limit: Int, $nextToken: String, $severity: String) {
    listImageCves(imagePlatformId: $imagePlatformId, limit: $limit, nextToken: $nextToken, severity: $severity) {
      items {
        id
        cveId
        title
        description
        severity
        score
        sourceUrl
        fixAvailable
        packageName
        packageVersion
        fixedVersion
      }
      nextToken
    }
  }
`;

export const GET_IMAGE_BENCHMARKS = gql`
  query GetImageBenchmarks($imageID: ID!) {
    getImageBenchmarks(imageID: $imageID) {
      id
      benchmarkName
      benchmarkVersion
      profile
      score
      maxScore
      passed
      failed
      notApplicable
      findings {
        ruleId
        title
        result
        severity
      }
    }
  }
`;

export const GET_BLUEPRINTS = gql`
  query GetBlueprints {
    listBlueprints {
      items {
        name
        version
        versions
        lastBuildAt
        lastAmiId
        description
        osType
        architecture
        thumbnailUrl
      }
    }
  }
`;

export const GET_BLUEPRINT_VERSIONS = gql`
  query GetBlueprintVersions {
    listBlueprintVersions
  }
`;

export const GET_BUILD_STATUS = gql`
  query GetBuildStatus($workflowName: String!) {
    getBuildStatus(workflowName: $workflowName) {
      name
      phase
      startedAt
      finishedAt
      progress
      nodes {
        id
        name
        podName
        phase
        message
        startedAt
        finishedAt
        templateName
      }
      parameters {
        name
        value
      }
    }
  }
`;

export const GET_BUILD_HISTORY = gql`
  query GetBuildHistory($limit: Int, $nextToken: String) {
    listBuildHistory(limit: $limit, nextToken: $nextToken) {
      items {
        name
        phase
        startedAt
        finishedAt
        progress
      }
      nextToken
    }
  }
`;

export const GET_SCHEDULES = gql`
  query GetSchedules {
    listSchedules {
      name
      schedule
      enabled
      lastRun
      nextRun
      blueprintName
    }
  }
`;

export const GET_COMPLIANCE_REPORT = gql`
  query GetComplianceReport($imageId: ID!) {
    getComplianceReport(imageId: $imageId) {
      imageId
      imageName
      blueprintName
      generatedAt
      overallScore
      cisLevel
      vulnerabilitySummary {
        CRITICAL
        HIGH
        MEDIUM
        LOW
        INFORMATIONAL
      }
      recommendations
      status
    }
  }
`;

export const GET_IMAGE_LIFECYCLE = gql`
  query GetImageLifecycle($imageId: ID!) {
    getImageLifecycle(imageId: $imageId) {
      imageId
      status
      createdAt
      deprecatedAt
      expiresAt
      replacedBy
      retentionDays
      autoCleanup
    }
  }
`;

export const GET_DEPRECATED_IMAGES = gql`
  query GetDeprecatedImages($limit: Int) {
    listDeprecatedImages(limit: $limit) {
      items {
        id
        name
        blueprintName
        status
        createdAt
        architecture
      }
      nextToken
    }
  }
`;

export const COMPARE_IMAGES = gql`
  query CompareImages($imageIdA: ID!, $imageIdB: ID!) {
    compareImages(imageIdA: $imageIdA, imageIdB: $imageIdB) {
      imageA { id name blueprintName blueprintVersion status createdAt }
      imageB { id name blueprintName blueprintVersion status createdAt }
      cvesDiff {
        addedCves { cveId severity title }
        removedCves { cveId severity title }
        unchangedCount
      }
      benchmarkDiff {
        scoreA
        scoreB
        newPasses
        newFailures
      }
      packageDiff {
        packageName
        versionA
        versionB
        action
      }
    }
  }
`;

export const GET_BUILD_LOGS = gql`
  query GetBuildLogs($workflowName: String!, $stepName: String) {
    getBuildLogs(workflowName: $workflowName, stepName: $stepName)
  }
`;

export const GET_NOTIFICATION_CONFIGS = gql`
  query GetNotificationConfigs {
    listNotificationConfigs {
      id
      channel
      webhookUrl
      events
      enabled
    }
  }
`;

// PR-1: Health Scorecards
export const GET_IMAGE_HEALTH_SCORE = gql`
  query GetImageHealthScore($imageId: ID!) {
    getImageHealthScore(imageId: $imageId) {
      imageId
      imageName
      overallScore
      grade
      cveScore
      ageScore
      cisScore
      sbomScore
      signingScore
      breakdown {
        cveWeight
        ageWeight
        cisWeight
        sbomWeight
        signingWeight
        criticalCveCount
        highCveCount
        imageAgeDays
        cisPassed
        cisFailed
        hasSbom
        isSigned
      }
      recommendations
    }
  }
`;

export const GET_IMAGE_HEALTH_SCORES = gql`
  query GetImageHealthScores($limit: Int) {
    listImageHealthScores(limit: $limit) {
      imageId
      imageName
      overallScore
      grade
      cveScore
      ageScore
      cisScore
      sbomScore
      signingScore
      recommendations
    }
  }
`;

// PR-2: SBOM
export const GET_IMAGE_SBOM = gql`
  query GetImageSBOM($imageId: ID!, $format: String) {
    getImageSBOM(imageId: $imageId, format: $format) {
      imageId
      format
      componentCount
      components {
        name
        version
        type
        purl
        licenses
      }
      downloadUrl
      generatedAt
    }
  }
`;

export const SEARCH_SBOM_COMPONENTS = gql`
  query SearchSBOMComponents($query: String!, $limit: Int) {
    searchSBOMComponents(query: $query, limit: $limit) {
      name
      version
      type
      purl
      licenses
    }
  }
`;

// PR-3: Image Lineage
export const GET_LINEAGE_GRAPH = gql`
  query GetLineageGraph($imageId: ID!) {
    getLineageGraph(imageId: $imageId) {
      nodes {
        id
        name
        blueprintName
        status
        createdAt
        amiId
      }
      edges {
        source
        target
      }
    }
  }
`;

export const FIND_AFFECTED_IMAGES = gql`
  query FindAffectedImages($baseAmiId: String!) {
    findAffectedImages(baseAmiId: $baseAmiId) {
      id
      name
      amiId
      blueprintName
      status
      createdAt
    }
  }
`;

// PR-4: Channels
export const GET_CHANNELS = gql`
  query GetChannels {
    listChannels {
      name
      description
      currentImageId
      currentAmiId
      autoPromote
      createdAt
      updatedAt
      history {
        imageId
        amiId
        promotedAt
        promotedBy
      }
    }
  }
`;

export const GET_CHANNEL = gql`
  query GetChannel($name: String!) {
    getChannel(name: $name) {
      name
      description
      currentImageId
      currentAmiId
      autoPromote
      createdAt
      updatedAt
      history {
        imageId
        amiId
        promotedAt
        promotedBy
      }
    }
  }
`;

// PR-5: Policy Engine
export const GET_POLICIES = gql`
  query GetPolicies {
    listPolicies {
      id
      name
      description
      rules
      severity
      enabled
      createdAt
    }
  }
`;

export const EVALUATE_IMAGE = gql`
  query EvaluateImage($imageId: ID!) {
    evaluateImage(imageId: $imageId) {
      imageId
      overallPass
      evaluatedAt
      results {
        policyId
        policyName
        pass
        severity
        message
        violations
      }
    }
  }
`;

// PR-6: Signing
export const GET_SIGNING_STATUS = gql`
  query GetSigningStatus($imageId: ID!) {
    getSigningStatus(imageId: $imageId) {
      imageId
      signed
      signatureUrl
      provenanceUrl
      slsaLevel
      signedAt
    }
  }
`;

// PR-7: Test Results
export const GET_TEST_RESULTS = gql`
  query GetTestResults($imageId: ID!) {
    getTestResults(imageId: $imageId) {
      imageId
      status
      framework
      totalTests
      passed
      failed
      skipped
      results {
        name
        status
        message
        duration
      }
      executedAt
    }
  }
`;

// PR-8: Replication
export const GET_REPLICATION_STATUS = gql`
  query GetReplicationStatus($imageId: ID!) {
    getReplicationStatus(imageId: $imageId) {
      imageId
      sourceRegion
      replications {
        region
        amiId
        status
        progress
        startedAt
        completedAt
      }
    }
  }
`;

// PR-9: Fleet Tracking
export const GET_FLEET_STATS = gql`
  query GetFleetStats {
    getFleetStats {
      totalInstances
      byImage {
        imageId
        imageName
        amiId
        count
      }
      ageDistribution {
        label
        count
      }
      driftCount
    }
  }
`;

export const GET_DEPLOYED_INSTANCES = gql`
  query GetDeployedInstances($imageId: ID!) {
    getDeployedInstances(imageId: $imageId) {
      instanceId
      amiId
      region
      accountId
      instanceType
      state
      launchTime
      tags {
        key
        value
      }
    }
  }
`;

export const GET_INSTANCES_BY_AMIS = gql`
  query GetInstancesByAMIs($amiIds: [String!]!) {
    getInstancesByAMIs(amiIds: $amiIds) {
      totalInstances
      totalAMIs
      totalRegions
      groups {
        amiId
        totalInstances
        regions {
          region
          instanceCount
          accounts {
            accountId
            instanceCount
            instances {
              instanceId
              amiId
              region
              accountId
              instanceType
              state
              launchTime
              tags {
                key
                value
              }
            }
          }
        }
      }
    }
  }
`;
